import { Button, IconButton, Popover, Typography } from '@neo4j-ndl/react';
import { XMarkIconOutline } from '@neo4j-ndl/react/icons';
import { FleetManagementTypes } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { useCallback } from 'react';

import { hasLicenseIssues } from '../../../../../../packages/state/src/slices/fleet-management/types';
import { FilterDropdown } from '../../../logs/shared/components/log-filters/filter-dropdown';
import { capitalizeFirstLowerRest } from '../../../shared/ui-helpers';
import { tokenExpired, tokenExpirySoon } from '../../deployments/token-management';
import {
  type AvailableDeploymentFilters,
  type DeploymentFilters,
  STATE_FILTERS,
} from '../../hooks/use-deployment-filters';
import { hasServersWithOutdatedPlugin } from './server-status';

export const DeploymentFilter = ({
  open,
  anchorEl,
  handleClose,
  filters,
  onFilterChange,
  clearAllFilters,
  availableFilters,
  filteredDeployments,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  filters: DeploymentFilters;
  onFilterChange: (newValues: Partial<DeploymentFilters>) => void;
  clearAllFilters: () => void;
  availableFilters: AvailableDeploymentFilters;
  filteredDeployments: FleetManagementTypes.DeploymentDetails[];
}) => {
  const onDeploymentFilterChange = useCallback(
    (filterKey: keyof DeploymentFilters) => {
      return (newValues: string[]) => {
        onFilterChange({
          ...filters,
          [filterKey]: newValues,
        });
      };
    },
    [filters, onFilterChange],
  );

  const valueFormatter = useCallback(
    (value: string, filterKey: keyof DeploymentFilters) => {
      if (filterKey === 'status') {
        const count = filteredDeployments.filter((deployment) => deployment.status.toLowerCase() === value).length;
        return `${capitalizeFirstLowerRest(value)} (${count})`;
      }
      if (filterKey === 'version') {
        const count = filteredDeployments.filter((deployment) => deployment.dbms?.version === value).length;
        return `${capitalizeFirstLowerRest(value)} (${count})`;
      }
      if (filterKey === 'edition') {
        const count = filteredDeployments.filter((deployment) => deployment.dbms?.edition === value).length;
        return `${capitalizeFirstLowerRest(value)} (${count})`;
      }
      if (filterKey === 'packaging') {
        const count = filteredDeployments.filter((deployment) => deployment.dbms?.packaging === value).length;
        return `${capitalizeFirstLowerRest(value)} (${count})`;
      }
      const count = filteredDeployments.filter((deployment) => {
        if (value === STATE_FILTERS.TOKEN_EXPIRY_SOON.toString()) {
          return isNotNullish(deployment.token) && tokenExpirySoon(deployment.token);
        }
        if (value === STATE_FILTERS.TOKEN_EXPIRED.toString()) {
          return isNotNullish(deployment.token) && tokenExpired(deployment.token);
        }
        if (value === STATE_FILTERS.LICENSE_ISSUE.toString()) {
          return hasLicenseIssues(deployment.dbms);
        }
        if (value === STATE_FILTERS.OUTDATED_PLUGIN.toString()) {
          return hasServersWithOutdatedPlugin(deployment);
        }
        if (value === STATE_FILTERS.HAS_CONNECTION_URL.toString()) {
          return isNotNullish(deployment.connectionUrl);
        }
        return false;
      }).length;
      return `${capitalizeFirstLowerRest(value)} (${count})`;
    },
    [filteredDeployments],
  );

  return (
    <Popover
      isOpen={open}
      anchorElement={anchorEl}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      }}
    >
      <Popover.Content>
        <div className="flex w-[350px] flex-col gap-4 p-6">
          <div className="flex w-full items-center justify-between">
            <Typography variant="h6">Filter</Typography>
            <IconButton
              onClick={handleClose}
              ariaLabel="Close filter menu"
              size="medium"
              htmlAttributes={{
                title: 'Close filter menu',
              }}
              isClean={true}
            >
              <XMarkIconOutline />
            </IconButton>
          </div>
          <div className="gap-token-5 flex flex-col ">
            <FilterDropdown
              field="Status"
              allValues={Object.values(FleetManagementTypes.Api.DEPLOYMENT_STATUS)}
              selectedValues={filters.status}
              onChange={onDeploymentFilterChange('status')}
              valueFormatter={(value) => valueFormatter(value, 'status')}
            />
            <FilterDropdown
              field="Version"
              allValues={availableFilters.version}
              selectedValues={filters.version}
              onChange={onDeploymentFilterChange('version')}
              valueFormatter={(value) => valueFormatter(value, 'version')}
            />
            <FilterDropdown
              field="Edition"
              allValues={availableFilters.edition}
              selectedValues={filters.edition}
              onChange={onDeploymentFilterChange('edition')}
              valueFormatter={(value) => valueFormatter(value, 'edition')}
            />
            <FilterDropdown
              field="Packaging"
              allValues={availableFilters.packaging}
              selectedValues={filters.packaging}
              onChange={onDeploymentFilterChange('packaging')}
              valueFormatter={(value) => valueFormatter(value, 'packaging')}
            />
            <FilterDropdown
              field="State"
              allValues={Object.values(STATE_FILTERS)}
              selectedValues={filters.state}
              onChange={onDeploymentFilterChange('state')}
              valueFormatter={(value) => valueFormatter(value, 'state')}
            />
          </div>
          <div className="flex justify-end">
            <Button
              className="mt-token-4 w-fit"
              fill="text"
              color="danger"
              size="small"
              type="reset"
              aria-label="Reset filters"
              onClick={() => {
                clearAllFilters();
              }}
            >
              <Typography variant="body-medium" className="text-palette-danger-text font-bold">
                Reset
              </Typography>
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
};
